// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The Ground's colour lookup table: instance label → colour, plus how strongly
 * each label is ghosted (PLAN.md decision 5).
 *
 * The Bygning modes colour the building *per survey type*: every point of an
 * instance that belongs to a type takes the type's colour under the current
 * lens, the emphasised types stay bright, everything else lerps toward
 * `--color-ghost`, and picked parts take the accent. All of that is a function
 * of the instance id alone, so it is folded once into a table indexed by
 * label (`buildColorLut`, O(parts + max label)), and the scene's per-point work
 * is one table read (`compileLut` → `PointCloudScene.writeColors`).
 *
 * Pure: no three.js, no DOM. Colours are linear-light rgb triples in 0..1 —
 * the space a three.js colour attribute is in — resolved from CSS tokens by
 * the caller (`parseCssColor`).
 */

import type { SurveyType } from '../api/types';
import { srgbToLinear } from './decode';

/** A linear-light rgb triple, 0..1 per channel. */
export type Rgb = [number, number, number];

/** Tones in `ColorLut.ghost`: how far a label is pushed toward the ghost colour. */
export const TONE_FULL = 0;
/** Dimmed: keeps a tint of its own colour (a type outside the focus). */
export const TONE_DIM = 1;
/** Ghost: the ghost colour itself (unlabeled, unowned, or a type with no colour). */
export const TONE_GHOST = 2;

/** Labels above this are not tabulated (treated as unlabeled): a guard against a runaway table. */
export const MAX_LUT_LABEL = 1 << 20;

export interface ColorLut {
  /** The `Label` cloud the table is indexed by (the instances cloud). */
  cloud: string;
  /** label → rgb, stride 3; index 0 is unlabeled. Labels past the end read as 0. */
  map: Float32Array;
  /** label → tone (`TONE_*`); absent = every label `TONE_FULL`. */
  ghost?: Uint8Array;
  /**
   * A second label cloud (rooms) and the id — or ids, for a storey focus — to
   * keep bright; points elsewhere fade toward the surface, or with `hide` are
   * not drawn at all (a room focus without "Vis omgivelser").
   */
  secondary?: { cloud: string; keep: RoomKeep; hide?: boolean } | null;
}

/** The room label(s) a room or storey focus keeps bright: one id, or a list (empty = none). */
export type RoomKeep = number | readonly number[];

/**
 * The page colours ghosting mixes with, read from the tokens by the viewport:
 * `--color-surface` (the Ground clears to it), `--color-ghost`, and
 * `--ghost-alpha` × the Skygge setting.
 */
export interface GhostStyle {
  surface: Rgb;
  ghost: Rgb;
  /** How much of the ghost colour shows over the surface, 0..1. */
  alpha: number;
}

/** A dimmed type keeps this much more of its tint than a pure ghost point (prototype: `ga * 1.6`). */
export const DIM_TINT_FACTOR = 1.6;
/** How much of a point outside the focused room survives over the surface (prototype: `rd = .25`). */
export const ROOM_OUT_KEEP = 0.3;

const clamp01 = (v: number) => (Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0);

/** `a + (b - a) * t`, per channel. */
export function mixRgb(a: Rgb, b: Rgb, t: number): Rgb {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

/**
 * A CSS colour value (as `getComputedStyle` returns a token) → linear rgb, or
 * null when it is not a form this reads: `#rgb`, `#rrggbb`, `#rrggbbaa`,
 * `rgb(r g b)`, `rgb(r, g, b)`, `rgba(…)` (alpha ignored).
 */
export function parseCssColor(value: string): Rgb | null {
  const text = value.trim().toLowerCase();
  let srgb: [number, number, number] | null = null;
  const hex = /^#([0-9a-f]{3,8})$/.exec(text);
  if (hex) {
    const h = hex[1];
    if (h.length === 3 || h.length === 4) {
      srgb = [0, 1, 2].map((i) => Number.parseInt(h[i] + h[i], 16) / 255) as [number, number, number];
    } else if (h.length === 6 || h.length === 8) {
      srgb = [0, 2, 4].map((i) => Number.parseInt(h.slice(i, i + 2), 16) / 255) as [number, number, number];
    }
  } else {
    const fn = /^rgba?\(\s*([^)]*)\)$/.exec(text);
    if (fn) {
      const parts = fn[1]
        .split(/[\s,/]+/)
        .filter(Boolean)
        .slice(0, 3)
        .map((p) => (p.endsWith('%') ? (Number.parseFloat(p) / 100) * 255 : Number.parseFloat(p)));
      if (parts.length === 3 && parts.every((p) => Number.isFinite(p))) {
        srgb = parts.map((p) => Math.min(255, Math.max(0, p)) / 255) as [number, number, number];
      }
    }
  }
  if (!srgb) return null;
  return [srgbToLinear(srgb[0]), srgbToLinear(srgb[1]), srgbToLinear(srgb[2])];
}

// ------------------------------------------------------------ survey → ids --

/** Whether a part's instance belongs to `cloud` (a part with no cloud is taken as the instances cloud). */
function onCloud(part: { cloud: string | null; instance_id: number | null }, cloud: string): part is { cloud: string | null; instance_id: number } {
  return part.instance_id !== null && part.instance_id > 0 && (part.cloud === null || part.cloud === cloud);
}

/** instance id → the part (and its type) it is filed under. The last writer wins on a duplicate. */
export function instanceOwners(
  types: readonly Pick<SurveyType, 'id' | 'parts'>[],
  cloud: string,
): Map<number, { typeId: number; code: string }> {
  const out = new Map<number, { typeId: number; code: string }>();
  for (const t of types) {
    for (const p of t.parts) if (onCloud(p, cloud)) out.set(p.instance_id, { typeId: t.id, code: p.code });
  }
  return out;
}

/** The instance ids of the parts with these codes (unknown codes and geometry-less parts are skipped). */
export function partInstances(
  types: readonly Pick<SurveyType, 'parts'>[],
  codes: readonly string[],
  cloud: string,
): number[] {
  if (codes.length === 0) return [];
  const want = new Set(codes);
  const out: number[] = [];
  for (const t of types) {
    for (const p of t.parts) if (want.has(p.code) && onCloud(p, cloud)) out.push(p.instance_id);
  }
  return out;
}

/** The most common `part.cloud` among the survey's parts: which `Label` cloud the instance ids refer to. */
export function dominantPartCloud(types: readonly Pick<SurveyType, 'parts'>[]): string | null {
  const counts = new Map<string, number>();
  for (const t of types) for (const p of t.parts) if (p.cloud) counts.set(p.cloud, (counts.get(p.cloud) ?? 0) + 1);
  let best: string | null = null;
  let n = 0;
  for (const [cloud, c] of counts) if (c > n) [best, n] = [cloud, c];
  return best;
}

// --------------------------------------------------------------- building --

/** One survey type as the table sees it. */
export interface LutTypeInput {
  id: number;
  /** Instance ids of its parts on the table's cloud. */
  instances: readonly number[];
  /** Its colour in the current mode/lens; null = no colour (ghost). */
  color: Rgb | null;
}

export interface ColorLutInput {
  /** The instances `Label` cloud. */
  cloud: string;
  types: readonly LutTypeInput[];
  /**
   * The emphasised types: null = no focus (every type in its colour); a list =
   * those types bright, the rest dimmed (or ghosted when they have no colour).
   */
  focusTypeIds: readonly number[] | null;
  /** Instances of picked parts (and the part in focus): the accent, whatever their type. */
  selectedInstances: readonly number[];
  /**
   * Instances outside Ressourcer's filtered set: ghost, whatever their type
   * (a pick still wins). Absent = none.
   */
  ghostInstances?: readonly number[];
  colors: {
    ghost: Rgb;
    accent: Rgb;
    /** A focused type that has no colour under the current lens (e.g. "I kø") still needs one. */
    neutral: Rgb;
  };
  /** Rapport: the building is a quiet backdrop — everything ghost. */
  quiet?: boolean;
  /**
   * Room focus (one id) or storey focus (its room ids): points of other rooms
   * fade (see `compileLut`), or with `hide` are left out of the draw (`applyLut`).
   */
  room?: { cloud: string; keep: RoomKeep; hide?: boolean } | null;
}

/**
 * Fold the survey and the selection into a `ColorLut`.
 *
 * - Label 0, labels no part owns, and types with no colour: ghost.
 * - No focus: each type in its colour, full.
 * - A focus: focused types full (their own colour, or `neutral`); other types
 *   dimmed toward the ghost but still tinted.
 * - Picked parts: the accent, full; while any are picked the focused types'
 *   other parts dim, so the picks stand out (the prototype halves them). Points
 *   cannot be drawn bigger one by one, so the accent is the whole signal.
 * - `ghostInstances` (outside Ressourcer's filters): ghost, before the picks.
 * - `quiet`: everything ghost; no picks.
 *
 * Cost: O(total parts + largest instance id). The table has one entry per
 * label up to the largest instance id a part references (capped at
 * `MAX_LUT_LABEL`); labels past it read as unlabeled.
 */
export function buildColorLut(input: ColorLutInput): ColorLut {
  let maxId = 0;
  for (const t of input.types) for (const id of t.instances) if (id > maxId && id < MAX_LUT_LABEL) maxId = id;
  for (const id of input.selectedInstances) if (id > maxId && id < MAX_LUT_LABEL) maxId = id;
  const size = maxId + 1;

  const map = new Float32Array(size * 3);
  const ghost = new Uint8Array(size).fill(TONE_GHOST);
  const g = input.colors.ghost;
  for (let i = 0; i < size; i += 1) {
    map[i * 3] = g[0];
    map[i * 3 + 1] = g[1];
    map[i * 3 + 2] = g[2];
  }
  const set = (id: number, c: Rgb, tone: number) => {
    map[id * 3] = c[0];
    map[id * 3 + 1] = c[1];
    map[id * 3 + 2] = c[2];
    ghost[id] = tone;
  };

  const secondary = input.room ?? null;
  if (input.quiet) return { cloud: input.cloud, map, ghost, secondary };

  const focus = input.focusTypeIds === null ? null : new Set(input.focusTypeIds);
  const picked = new Set(input.selectedInstances);
  const anyPicked = picked.size > 0;

  for (const t of input.types) {
    for (const id of t.instances) {
      if (id <= 0 || id >= size) continue;
      if (focus === null) {
        if (t.color) set(id, t.color, TONE_FULL);
        else set(id, g, TONE_GHOST);
      } else if (focus.has(t.id)) {
        set(id, t.color ?? input.colors.neutral, anyPicked ? TONE_DIM : TONE_FULL);
      } else if (t.color) {
        set(id, t.color, TONE_DIM);
      } else {
        set(id, g, TONE_GHOST);
      }
    }
  }
  for (const id of input.ghostInstances ?? []) if (id > 0 && id < size) set(id, g, TONE_GHOST);
  for (const id of picked) if (id > 0 && id < size) set(id, input.colors.accent, TONE_FULL);

  return { cloud: input.cloud, map, ghost, secondary };
}

/** The two per-label colour tables the scene reads point by point. */
export interface CompiledLut {
  /** Labels in the focus (or with no room focus): final rgb, stride 3. */
  full: Float32Array;
  /** The same labels on points outside the focused room. */
  out: Float32Array;
  /**
   * label → 1 when its points in `full` are ghost (`TONE_GHOST`). Every
   * point read from `out` (outside a room/storey focus) is ghost too. Ghost
   * points draw in the scene's see-through pass (frontend-r22.22).
   */
  ghost: Uint8Array;
  /** Entries per table. */
  size: number;
}

/** The lowest ghost opacity the scene takes: below it a ghost wall vanishes outright. */
export const MIN_GHOST_OPACITY = 0.05;

/**
 * Resolve a table's tones against the page colours, once per LUT or theme
 * change, so the per-point pass is a lookup:
 *
 *   ghostEff = mix(surface, ghost, alpha)       — a ghost point on the surface
 *   FULL     = colour
 *   DIM      = mix(ghostEff, colour, alpha × DIM_TINT_FACTOR)
 *   GHOST    = ghostEff
 *   out      = mix(surface, tone result, ROOM_OUT_KEEP)
 *
 * `ghost` flags the GHOST entries; they and every `out` entry draw in the
 * scene's see-through pass, in these same colours: where ghost points stack
 * (a wall seen edge-on) they build up to the opaque ghost, never past it, and
 * a single layer lets a labelled point behind it show.
 */
export function compileLut(lut: ColorLut, style: GhostStyle): CompiledLut {
  const size = Math.max(1, Math.floor(lut.map.length / 3));
  const full = new Float32Array(size * 3);
  const out = new Float32Array(size * 3);
  const ghost = new Uint8Array(size);
  const alpha = clamp01(style.alpha);
  const ghostEff = mixRgb(style.surface, style.ghost, alpha);
  const tint = clamp01(alpha * DIM_TINT_FACTOR);
  for (let i = 0; i < size; i += 1) {
    const c: Rgb = lut.map.length >= (i + 1) * 3 ? [lut.map[i * 3], lut.map[i * 3 + 1], lut.map[i * 3 + 2]] : ghostEff;
    const tone = lut.ghost ? (lut.ghost[i] ?? TONE_FULL) : TONE_FULL;
    const isGhost = tone === TONE_GHOST;
    ghost[i] = isGhost ? 1 : 0;
    const f = isGhost ? ghostEff : tone === TONE_DIM ? mixRgb(ghostEff, c, tint) : c;
    const o = mixRgb(style.surface, f, ROOM_OUT_KEEP);
    full[i * 3] = f[0];
    full[i * 3 + 1] = f[1];
    full[i * 3 + 2] = f[2];
    out[i * 3] = o[0];
    out[i * 3 + 1] = o[1];
    out[i * 3 + 2] = o[2];
  }
  return { full, out, ghost, size };
}

/**
 * Colour `count` points from a compiled table into `target` (rgb stride 3).
 * The scene's per-point loop, kept here so it is tested: one or two array
 * reads per point. `labels` null → every point reads entry 0 (unlabeled).
 * `secondary.keep` is one room id (room focus) or several (storey focus);
 * with several, the set lookup is cached per run of equal room labels (a
 * page is spatially coherent), so it stays cheap per point.
 *
 * With `order`, also partitions the points into solid and ghost (label
 * `TONE_GHOST`, or outside the room focus): `order[0..solid)` are the solid
 * points, `order[solid..drawn)` the ghost ones. With `hideOut` (and a room
 * focus) the points outside it are not drawn at all: they go to
 * `order[drawn..count)`, past both passes' ranges. Without `hideOut`,
 * `drawn = count`; without `order`, `solid = drawn = count`.
 */
export function applyLut(
  target: Float32Array,
  count: number,
  table: CompiledLut,
  labels: Uint32Array | null,
  secondary: { labels: Uint32Array; keep: RoomKeep } | null,
  order: Uint32Array | null = null,
  hideOut = false,
): { solid: number; drawn: number } {
  const { full, out, ghost, size } = table;
  const sec = secondary && secondary.labels.length >= count ? secondary : null;
  // One id compares directly; a list becomes a set. -1 never equals a Uint32 label.
  const keep = sec?.keep;
  const keepOne = typeof keep === 'number' ? keep : keep && keep.length === 1 ? keep[0] : -1;
  const keepSet = keep !== undefined && typeof keep !== 'number' && keep.length !== 1 ? new Set(keep) : null;
  let lastRoom = -1;
  let lastIn = false;
  // Hiding: count the points outside the focus first, so the in-focus ghosts
  // can fill backwards from where the hidden ones start.
  const hide = hideOut && sec !== null && order !== null;
  let hidden = 0;
  if (hide && sec) {
    for (let i = 0; i < count; i += 1) {
      const room = sec.labels[i];
      if (keepSet) {
        if (room !== lastRoom) {
          lastRoom = room;
          lastIn = keepSet.has(room);
        }
        if (!lastIn) hidden += 1;
      } else if (room !== keepOne) {
        hidden += 1;
      }
    }
    lastRoom = -1;
    lastIn = false;
  }
  // `order` (length ≥ count): solid point indices from the front, ghost ones
  // backwards from `count - hidden`, hidden ones backwards from `count`, so
  // one index buffer serves both passes.
  let front = 0;
  let back = count - hidden;
  let outBack = count;
  for (let i = 0; i < count; i += 1) {
    const label = labels && i < labels.length ? labels[i] : 0;
    const entry = label < size ? label : 0;
    const at = entry * 3;
    let src = full;
    if (sec) {
      const room = sec.labels[i];
      if (keepSet) {
        if (room !== lastRoom) {
          lastRoom = room;
          lastIn = keepSet.has(room);
        }
        if (!lastIn) src = out;
      } else if (room !== keepOne) {
        src = out;
      }
    }
    target[i * 3] = src[at];
    target[i * 3 + 1] = src[at + 1];
    target[i * 3 + 2] = src[at + 2];
    if (order) {
      if (hide && src === out) {
        outBack -= 1;
        order[outBack] = i;
      } else if (src === out || ghost[entry] === 1) {
        back -= 1;
        order[back] = i;
      } else {
        order[front] = i;
        front += 1;
      }
    }
  }
  return order ? { solid: front, drawn: count - hidden } : { solid: count, drawn: count };
}

/** `order[0..count)` = 0, 1, …: every point solid (RGB / label modes have no ghost). Returns `count`. */
export function identityOrder(order: Uint32Array, count: number): number {
  for (let i = 0; i < count; i += 1) order[i] = i;
  return count;
}
